'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { Button, Modal } from '@insta-club/ui';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

interface Feature {
    data: {
        id: number;
        title: string;
        version: string;
        image: string;
        options: {
            id: number;
            title: string;
            link?: string;
        }[];
        created_at: string;
    };
    success: boolean;
}

export default function UpdateAnnouncementModal() {
    const pathName = usePathname();
    const includedPaths = ['/dashboard', '/analysis', '/explore', '/academy', '/profile'];
    const isIncludedPaths = includedPaths.includes(pathName);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const { data, isLoading } = useSWR<Feature>(isIncludedPaths ? ApiKeys.GET_FEATURES : null, (url: string) =>
        fetcher(url, 'dashboard', 'GET')
    );

    useEffect(() => {
        if (data?.data) {
            setIsOpen(true);
        }
    }, [data]);

    const handleClose = async () => {
        try {
            setLoading(true);
            await fetcher(ApiKeys.GET_FEATURES_SEEN(data?.data.id ?? 0), 'dashboard', 'GET');
            setIsOpen(false);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    if (!isIncludedPaths) return null;

    if (isLoading) return;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title='به روزرسانی اینستاکلاب'
            description=''
            closeOnOverlayClick={false}
        >
            <div className='space-y-6'>
                <div className='w-full overflow-hidden rounded-xl'>
                    <Image
                        src={data?.data?.image ?? '/img-not-found-placeholder.jpg'}
                        alt='Update announcement'
                        className='h-48 w-full object-cover'
                        width={1}
                        height={1}
                        unoptimized
                    />
                </div>

                <div className=''>
                    <h3 className='flex-1 truncate pl-1 text-base font-bold text-gray-800'>{data?.data?.title}</h3>

                    <span className='text-sm font-medium text-gray-600'>
                        <span className='ml-1'>به روزرسانی به نسخه:</span>
                        <span className='text-xs text-gray-800' style={{ fontFamily: 'Poppins' }}>
                            {data?.data?.version}
                        </span>
                    </span>
                </div>

                <div className='space-y-2'>
                    <h4 className='mb-3 text-sm font-semibold text-gray-700'>لیست تغییرات:</h4>
                    <ul className=''>
                        {data?.data?.options.map((item, index) => (
                            <li key={index} className='flex items-center justify-between'>
                                <div className='flex w-4/5 items-center gap-2'>
                                    <h6 className='h-1 w-1 flex-shrink-0 rounded-full bg-indigo-600'></h6>
                                    <h6 className='text-xs leading-[20px] font-medium text-gray-800'>{item.title}</h6>
                                </div>

                                <div className='min-h-[25px]'>
                                    {item?.link && (
                                        <a
                                            href={item.link}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='flex items-center gap-2 rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium text-blue-900'
                                        >
                                            کلیک کنید
                                            <svg
                                                width='8'
                                                height='8'
                                                viewBox='0 0 13 13'
                                                fill='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <path
                                                    d='M0.5 0.5L12.1667 12.1667'
                                                    stroke='currentColor'
                                                    strokeMiterlimit='10'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                />
                                                <path
                                                    d='M0.5 9.05833V0.5H9.05833'
                                                    stroke='currentColor'
                                                    strokeMiterlimit='10'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                />
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* دکمه متوجه شدم */}
                <Button loading={loading} onClick={handleClose} fullWidth size='sm' color='secondary'>
                    متوجه شدم
                </Button>
            </div>
        </Modal>
    );
}

